import dayjs from 'utils/dayjs';
import {spotheroPost, spotheroPatch} from 'api';
import {sendSentryException} from 'utils/error-handling';
import {camelizeKeys, convertKeysToSnakeCase} from 'utils/format-strings';

import {displayDateFormat, displayTimeMeridiem} from 'utils/date-time';
import {
    EXCEPTION,
    CONTINUOUS,
} from 'pages/inventory/utils/inventory-rule-types';

import {
    NEVER_END,
    SPECIFIC_DATE,
} from 'pages/inventory/components/ExceptionModal/state/exception-modal-state';
import {
    type DaysOfWeek,
    type DaysOfWeekCheckboxValues,
    type InventoryRule,
} from 'api/inventory/inventory.types';
import {type RawApiResponse} from 'api/api.types';

interface AddInventoryExceptionParams {
    facilityId: number;
    ruleId?: number;
    title: string;
    endDate: string;
    quantity: string | number | null | undefined;
    startDate: string;
    status?: string;
    endTime?: string;
    startTime?: string;
    scheduleType?: string;
    repeatEndType?: string;
    repeatEndDate?: string;
    daysOfWeek?: DaysOfWeekCheckboxValues;
}

interface AddInventoryExceptionRequest {
    title: string;
    status: string;
    quantity: number;
    validTo: string | null;
    validFrom: string;
    validToTime: string;
    validFromTime: string;
    validToDate: string | null;
    validFromDate: string;
    scheduleType: string;
    endTime: string;
    startTime: string;
    continuousStartDow: number | null;
    continuousEndDow: number | null;
    monday: boolean;
    tuesday: boolean;
    wednesday: boolean;
    thursday: boolean;
    friday: boolean;
    saturday: boolean;
    sunday: boolean;
}

type AddInventoryExceptionRawRequest =
    RawApiResponse<AddInventoryExceptionRequest>;
type AddInventoryExceptionRawResponse = RawApiResponse<InventoryRule>;

/**
 * Transforms the days of the week object from a toggle group to the format expected by the API
 * @function transformDaysOfWeek
 *
 * @param daysOfWeek
 *
 * @memberof module:api/inventory/dto
 */
export const transformDaysOfWeek = (
    daysOfWeek: DaysOfWeekCheckboxValues
): DaysOfWeek => {
    const keys = Object.keys(daysOfWeek);

    if (keys.length === 0) {
        return;
    }

    return keys.reduce(
        (acc, day) => {
            acc[day] = daysOfWeek[day] === 'on';

            return acc;
        },
        {
            monday: false,
            tuesday: false,
            wednesday: false,
            thursday: false,
            friday: false,
            saturday: false,
            sunday: false,
        }
    );
};

/**
 * Converts datetime to an ISO string
 * @private
 *
 * @param date - the date as "MM/DD/YYYY" that should be formatted
 * @param  time - the time as "h:mm A" that should be formatted
 * @returns The datetime in ISO format
 *
 * @memberof module:api/inventory/dto
 */
const datetimeToISOString = (date: string, time: string) => {
    return dayjs(
        `${date} ${time}`,
        `${displayDateFormat} ${displayTimeMeridiem}`
    ).toISOString();
};

/**
 * Formats a date to the format expected by the API
 *
 * @private
 * @param {string} date - the date that should be formatted
 * @returns {string | undefined}
 *
 * @memberof module:api/inventory/dto
 */

const formatDate = (date: string): string => {
    const formattedDate = dayjs(date, 'MM/DD/YYYY, ddd').format('MM/DD/YYYY');

    return formattedDate;
};

/** c
 * Formats a time to the format expected by the API
 * @private
 *
 * @param  time - the time that should be formatted
 * @returns The time in HH:mm format
 *
 * @memberof module:api/inventory/dto
 */
const formatTime = (time: string) => {
    const formattedTime = dayjs(time, displayTimeMeridiem).format('HH:mm');

    return formattedTime;
};

/**
 * Formats a date to a weekday in the format expected by the API
 * @private
 * @function
 *
 * @param date - the date that should be formatted
 *
 * @memberof module:api/inventory/dto
 */
export const toWeekday = (date: string) => {
    const formattedValue =
        (dayjs(date, `${displayDateFormat}, ddd`).weekday() + 6) % 7;

    return formattedValue;
};

/**
 * Formats the request object for adding/updating inventory exceptions
 * @param request
 */
const formatAddInventoryExceptionRequest = (
    request: AddInventoryExceptionParams
): AddInventoryExceptionRawRequest => {
    const {
        title,
        endDate,
        quantity,
        startDate,
        status = 'enabled',
        endTime = '11:59 PM',
        startTime = '12:00 AM',
        scheduleType = EXCEPTION,
        repeatEndType,
        repeatEndDate,
        daysOfWeek = {},
    } = request;

    const formattedStartDate = formatDate(startDate);
    const formattedEndDate = formatDate(endDate);
    let validTo: string | null;
    let validToTime: string;
    let validToDate: string | null;
    let validFrom = datetimeToISOString(formattedStartDate, '12:00 AM');
    let validFromTime = '12:00 AM';
    let formattedStartTime = formatTime(startTime);
    let formattedEndTime = formatTime(endTime);

    if (repeatEndType === SPECIFIC_DATE) {
        validToDate = formatDate(repeatEndDate);
        validTo = datetimeToISOString(validToDate, '11:59 PM');
        validToTime = '11:59 PM';
    } else if (repeatEndType === NEVER_END) {
        validTo = null;
        validToDate = null;
        validToTime = '11:59 PM';
    } else {
        validTo = datetimeToISOString(formattedEndDate, '11:59 PM');
        validToDate = formattedEndDate;
        validToTime = '11:59 PM';
    }

    if (scheduleType === EXCEPTION) {
        validFrom = datetimeToISOString(formattedStartDate, startTime);
        validToDate = formattedEndDate;
        validTo = datetimeToISOString(validToDate, endTime);
        validToTime = endTime;
        validFromTime = startTime;
        formattedEndTime = '00:00';
        formattedStartTime = '00:00';
    }

    const data: AddInventoryExceptionRequest = {
        title,
        status,
        quantity: Number(quantity),
        validTo,
        validFrom,
        validToTime,
        validFromTime,
        validToDate,
        validFromDate: formattedStartDate,
        scheduleType,
        endTime: formattedEndTime,
        startTime: formattedStartTime,
        continuousStartDow:
            scheduleType === CONTINUOUS ? toWeekday(startDate) : null,
        continuousEndDow:
            scheduleType === CONTINUOUS ? toWeekday(endDate) : null,
        ...transformDaysOfWeek(daysOfWeek),
    };

    const formattedRequest = convertKeysToSnakeCase(data);

    return formattedRequest;
};

/**
 * Formats the response object for adding/updating inventory exceptions
 * @param response
 */
const formatAddInventoryExceptionResponse = (
    response: AddInventoryExceptionRawResponse
): InventoryRule => {
    const formattedResponse = camelizeKeys(response);

    return formattedResponse;
};

/**
 * Adds or updates an inventory exception for a given facility
 *
 * @param params - The exception parameters
 * @returns Promise<InventoryRule>
 * @throws {Error}
 *
 * @see https://spothero.com/api/v1/docs/endpoints/?new-docs=true#!/Facilities/post_facilities_facility_id_inventory_rules
 * @see https://spothero.com/api/v1/docs/endpoints/?new-docs=true#!/Facilities/patch_facilities_facility_id_inventory_rules_rule_id
 * @see '/api/v1/facilities/{facilityId}/inventory-rules/'
 * @see '/api/v1/facilities/{facilityId}/inventory-rules/{ruleId}/'
 */
const addInventoryException = async (
    params: AddInventoryExceptionParams
): Promise<InventoryRule> => {
    try {
        const {facilityId, ruleId} = params;
        const formattedRequest = formatAddInventoryExceptionRequest(params);
        const apiMethod = ruleId ? spotheroPatch : spotheroPost;
        const apiEndpoint = ruleId
            ? `facilities/${facilityId}/inventory-rules/${ruleId}/`
            : `facilities/${facilityId}/inventory-rules/`;

        const response = await apiMethod<AddInventoryExceptionRawResponse>(
            apiEndpoint,
            formattedRequest
        );

        const formattedResponse = formatAddInventoryExceptionResponse(
            response.data.data
        );

        return formattedResponse;
    } catch (err) {
        sendSentryException(err);

        throw new Error(err.message);
    }
};

export default addInventoryException;
